import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  ArrowRight, Globe2, Zap, ShieldCheck, Gift, Award, Gamepad2, Plane, UtensilsCrossed,
  Store, Building2, Users, HeartHandshake, MonitorPlay, Trophy, ChevronRight,
} from "lucide-react";
import EnergyCanvas from "@/components/EnergyCanvas";
import ArticleCover from "@/components/ArticleCover";
import BrandLogo, { BrandMarquee } from "@/components/BrandLogo";
import Reveal, { SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { useSEO } from "@/hooks/useSEO";

const heroStats = [
  { icon: Award, value: "500+ Global Brands", label: "Leading retail & gaming partners" },
  { icon: Globe2, value: "120+ Countries", label: "Worldwide digital distribution" },
  { icon: Zap, value: "Instant Delivery", label: "Digital fulfillment within seconds" },
  { icon: ShieldCheck, value: "Reliable Distribution", label: "Trusted enterprise fulfillment" },
];

const trustStats = [
  { value: "500+", label: "Digital Brands" },
  { value: "120+", label: "Countries" },
  { value: "24/7", label: "Digital Fulfillment" },
  { value: "B2B", label: "Enterprise Solutions" },
];

const CHIP_LOGOS = {
  steam: "https://cdn.simpleicons.org/steam",
  playstation: "https://cdn.simpleicons.org/playstation",
  apple: "https://cdn.simpleicons.org/apple",
  xbox: "https://cdn.worldvectorlogo.com/logos/xbox-9.svg",
  netflix: "https://cdn.simpleicons.org/netflix",
  spotify: "https://cdn.simpleicons.org/spotify",
};

const floatChips = [
  { slug: "steam", top: "4%", right: "6%", depth: 46, rotate: -8, size: 84 },
  { slug: "playstation", top: "26%", right: "22%", depth: -30, rotate: 6, size: 72 },
  { slug: "apple", top: "38%", right: "4%", depth: 34, rotate: -5, size: 64 },
  { slug: "xbox", top: "55%", right: "18%", depth: -42, rotate: 9, size: 76 },
  { slug: "netflix", top: "68%", right: "5%", depth: 26, rotate: -7, size: 62 },
  { slug: "spotify", top: "16%", right: "36%", depth: -22, rotate: 5, size: 58 },
];

const solutions = [
  {
    icon: Gift,
    title: "Digital Gift Cards",
    body: "Deliver branded digital gift cards from the world's leading retail, entertainment, lifestyle, and shopping brands.",
  },
  {
    icon: Award,
    title: "Corporate Rewards",
    body: "Motivate employees, reward customers, and strengthen loyalty with personalized digital gifting campaigns.",
  },
  {
    icon: Gamepad2,
    title: "Gaming Vouchers",
    body: "Access popular gaming subscriptions, in-game currency, and entertainment vouchers with reliable fulfillment.",
  },
];

const categories = [
  { icon: Store, title: "Retail & Lifestyle", logos: ["amazon", "apple", "ebay", "gamestop", "uber"] },
  { icon: Gamepad2, title: "Gaming", logos: ["steam", "xbox", "playstation", "nintendo", "minecraft"] },
  { icon: MonitorPlay, title: "Streaming & Social", logos: ["spotify", "netflix", "twitch", "discord"] },
  { icon: Plane, title: "Travel & Transport", logos: ["careem", "uber", "ola"], brands: ["Airlines", "Hotels", "Travel experiences"] },
  { icon: UtensilsCrossed, title: "Food & Dining", logos: ["zomato", "swiggy"], brands: ["Talabat", "Game & Grub", "Restaurant vouchers", "Food delivery"] },
  { icon: Zap, title: "Mobile & Data", brands: ["Lebara", "Lyca Mobile", "Mobily", "Zain", "Virgin", "Ooredoo"] },
  { icon: ShieldCheck, title: "Software & Security", brands: ["Microsoft 365", "Windows 10", "McAfee", "NordVPN"] },
];

const whyVowpin = [
  { icon: Award, title: "Global Brand Catalog", body: "Hundreds of trusted international digital brands — retail, gaming, streaming, and lifestyle — ready for instant delivery." },
  { icon: Zap, title: "Instant Fulfillment", body: "Fast and seamless digital gift delivery worldwide, measured in seconds and available around the clock." },
  { icon: ShieldCheck, title: "Reliable Distribution", body: "Consistent fulfillment for enterprise gifting programs, with the service discipline premium partners expect." },
  { icon: Globe2, title: "Worldwide Reach", body: "Serving businesses across Europe, Asia, Middle East & Africa with localized catalogs for every market." },
];

const industries = [
  { icon: Store, label: "Retail & Ecommerce" },
  { icon: Building2, label: "Corporate Enterprises" },
  { icon: Users, label: "Employee Rewards" },
  { icon: HeartHandshake, label: "Customer Loyalty" },
  { icon: MonitorPlay, label: "Gaming Platforms" },
  { icon: Trophy, label: "Rewards Programs" },
];

const posts = [
  {
    tag: "Digital Gifting",
    slug: "digital-gift-cards-convenience-modern-consumers",
    icon: "gift",
    title: "Digital Gift Cards: The Convenience for Modern Consumers",
    brief: "Why instant, branded digital vouchers have become the default way the world gifts.",
  },
  {
    tag: "MENA",
    slug: "digital-payments-transform-businesses-mena",
    icon: "mappin",
    title: "How Digital Payments Transform Businesses in the MENA Region",
    brief: "Digital vouchers are reshaping how MENA businesses reward, incentivize, and grow.",
  },
  {
    tag: "Growth",
    slug: "5-reasons-localized-payment-solutions-improve-conversions",
    icon: "trending",
    title: "5 Reasons Localized Payment Solutions Improve Conversions",
    brief: "Local catalogs and familiar vouchers remove the friction that kills conversions.",
  },
];

function ParallaxChip({ chip, sx, sy, index }) {
  const x = useTransform(sx, (v) => v * chip.depth);
  const y = useTransform(sy, (v) => v * chip.depth);
  return (
    <motion.div
      className="absolute hidden lg:block"
      style={{ top: chip.top, right: chip.right, x, y, rotate: chip.rotate }}
      initial={{ opacity: 0, y: 30, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 1, delay: 0.9 + index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      aria-hidden="true"
    >
      <div
        className="flex items-center justify-center rounded-2xl border border-[#e8ecf4] bg-white shadow-[0_16px_40px_-16px_rgba(8,22,168,0.25)]"
        style={{ width: chip.size, height: chip.size }}
      >
        <img src={CHIP_LOGOS[chip.slug]} alt="" className="h-1/2 w-1/2 object-contain" />
      </div>
    </motion.div>
  );
}

export default function Home() {
  useSEO();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 22 });
  const sy = useSpring(my, { stiffness: 50, damping: 22 });

  const onHeroMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <main>
      {/* Hero */}
      <section
        className="relative flex min-h-[92vh] items-center overflow-hidden bg-white pt-24"
        onMouseMove={onHeroMouse}
      >
        <EnergyCanvas className="absolute inset-0 h-full w-full opacity-60" />
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white to-transparent" />

        {floatChips.map((chip, i) => (
          <ParallaxChip key={chip.slug} chip={chip} sx={sx} sy={sy} index={i} />
        ))}

        <div className="relative mx-auto w-full max-w-7xl px-6 py-20 lg:px-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -left-2 top-2 hidden h-48 w-1 origin-top rounded-full bg-gradient-to-b from-[#f4a900] via-[#0816a8]/50 to-transparent lg:block"
              aria-hidden="true"
            />
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center gap-2 rounded-full border border-[#f4a900]/40 bg-[#f4a900]/[0.08] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#b87e00]"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#f4a900] animate-pulse-glow" />
              Global Digital Gift Cards
            </motion.div>

            <h1 className="mt-8 font-display text-4xl font-semibold leading-[1.06] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl" data-testid="hero-title">
              <span className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
                >
                  Power Global
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-1">
                <motion.span
                  className="block text-gradient-blue"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, delay: 0.33, ease: [0.16, 1, 0.3, 1] }}
                >
                  Digital Gifting
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 max-w-2xl text-base leading-relaxed text-[#64748b] md:text-lg"
            >
              Deliver premium digital gift cards, gaming vouchers and corporate rewards across
              global markets — with speed, reliability, and premium service.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-talk-to-sales-btn"
                className="group flex items-center gap-2 rounded-full bg-[#0816a8] px-8 py-4 text-sm font-semibold text-white shadow-[0_10px_28px_-8px_rgba(8,22,168,0.5)] transition-all duration-300 hover:bg-[#0a1bc4] hover:shadow-[0_14px_36px_-8px_rgba(8,22,168,0.6)]"
              >
                Talk to Sales
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/products"
                data-testid="hero-request-catalogue-btn"
                className="rounded-full border border-[#d7dce8] bg-white px-8 py-4 text-sm font-semibold text-[#374151] transition-colors duration-300 hover:border-[#0816a8]/50 hover:text-[#0816a8]"
              >
                Request Catalogue
              </Link>
            </motion.div>
          </div>

          <div className="mt-20 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {heroStats.map((s, i) => (
              <motion.div
                key={s.value}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.9 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="glow-card spotlight rounded-[20px] p-6"
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                }}
                data-testid={`hero-stat-${i}`}
              >
                <s.icon className="h-8 w-8 text-[#0816a8]" strokeWidth={1.8} />
                <p className="mt-4 font-display text-lg font-semibold text-[#111827]">{s.value}</p>
                <p className="mt-1 text-sm text-[#64748b]">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust section */}
      <section className="border-y border-[#e8ecf4] bg-white">
        <div className="border-b border-[#e8ecf4] bg-white py-7">
          <BrandMarquee />
        </div>
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
          <Reveal className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c98a00]">Trusted by Global Businesses</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-10 lg:grid-cols-4">
            {trustStats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} className="text-center">
                <p className="font-display text-4xl font-semibold text-[#0816a8] sm:text-5xl" data-testid={`trust-stat-${i}`}>{s.value}</p>
                <p className="mt-2 text-sm text-[#64748b]">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Solutions"
            title="Smart Digital Reward Solutions"
            sub="Premium digital gifting for every business moment — from customer campaigns to employee recognition."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {solutions.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="glow-card spotlight h-full rounded-[20px] p-9" data-testid={`solution-card-${i}`}
                  onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                  }}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0816a8] shadow-[0_10px_24px_-8px_rgba(8,22,168,0.5)]">
                    <s.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-semibold text-[#111827]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#64748b] md:text-base">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/solutions"
              data-testid="solutions-explore-link"
              className="group mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#0816a8] transition-colors duration-200 hover:text-[#0a1bc4]"
            >
              Explore all solutions
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Featured categories */}
      <section className="relative border-y border-[#e8ecf4] bg-[#f7f9fc] py-24">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Featured Categories"
            title="Explore Our Global Catalog"
            sub="Hundreds of the world's most loved brands, ready for instant digital delivery."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.07}>
                <div className="glow-card flex h-full flex-col rounded-[20px] p-8" data-testid={`category-card-${i}`}>
                  <c.icon className="h-7 w-7 text-[#0816a8]" />
                  <h3 className="mt-5 font-display text-lg font-semibold text-[#111827]">{c.title}</h3>
                  {c.logos && (
                    <div className="mt-5 grid grid-cols-3 gap-2.5">
                      {c.logos.map((slug) => (
                        <BrandLogo key={slug} slug={slug} />
                      ))}
                    </div>
                  )}
                  {c.brands && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {c.brands.map((b) => (
                        <span key={b} className="rounded-full border border-[#e5e9f2] bg-[#f7f9fc] px-3 py-1 text-xs text-[#64748b]">
                          {b}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/products"
              data-testid="catalog-explore-link"
              className="group mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#0816a8] transition-colors duration-200 hover:text-[#0a1bc4]"
            >
              Browse the full catalog
              <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Why Vowpin — numbered manifesto */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Why Vowpin"
            title="Why Businesses Choose Vowpin"
          />
          <div className="mt-14 border-t border-[#e8ecf4]">
            {whyVowpin.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.06}>
                <div
                  className="group grid grid-cols-[auto_1fr] items-start gap-6 border-b border-[#e8ecf4] py-10 transition-colors duration-300 hover:bg-[#f7f9fc] md:grid-cols-[110px_1fr_1.1fr_auto] md:items-center lg:px-4"
                  data-testid={`why-chapter-${i}`}
                >
                  <span className="font-display text-4xl font-semibold text-[#0816a8]/[0.14] transition-colors duration-300 group-hover:text-[#0816a8]/50 sm:text-5xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-xl font-semibold text-[#111827] sm:text-2xl">{w.title}</h3>
                  <p className="col-span-2 max-w-xl text-sm leading-relaxed text-[#64748b] md:col-span-1 md:text-base">{w.body}</p>
                  <w.icon className="hidden h-6 w-6 text-[#c98a00] transition-colors duration-300 group-hover:text-[#f4a900] md:block" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="border-t border-[#e8ecf4] bg-[#f7f9fc] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Industries"
            title="Industries We Serve"
            sub="From retail floors to gaming lobbies, Vowpin powers gifting across every sector."
          />
          <div className="mt-12 flex flex-wrap gap-4">
            {industries.map((ind, i) => (
              <Reveal key={ind.label} delay={i * 0.05}>
                <div
                  className="flex items-center gap-3 rounded-full border border-[#e5e9f2] bg-white px-6 py-3.5 transition-colors duration-300 hover:border-[#0816a8]/40"
                  data-testid={`industry-chip-${i}`}
                >
                  <ind.icon className="h-5 w-5 text-[#0816a8]" />
                  <span className="text-sm font-semibold text-[#374151]">{ind.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Blog teasers */}
      <section className="border-t border-[#e8ecf4] bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Latest Insights"
            title="From the Vowpin Journal"
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {posts.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.07}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-teaser-${i}`} className="glow-card group flex h-full flex-col overflow-hidden rounded-[20px]">
                  <ArticleCover icon={p.icon} tag={p.tag} index={i} />
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-display text-lg font-semibold leading-snug text-[#111827] transition-colors duration-200 group-hover:text-[#0816a8]">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-[#64748b]">{p.brief}</p>
                    <span className="mt-auto pt-5 text-xs font-semibold uppercase tracking-widest text-[#9aa3b5] transition-colors duration-200 group-hover:text-[#c98a00]">
                      Read article
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/blog"
              data-testid="blog-view-all-link"
              className="group mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#0816a8] transition-colors duration-200 hover:text-[#0a1bc4]"
            >
              View all insights
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
